/*
  HOK core — product page components that the theme loaded from an external file
  (deshawnippin8888.serv00.net/main.js), now blocked by its host. Without them, the quantity
  buttons, the variant pickers and the sticky add-to-cart bar do nothing when tapped.
  Each component is defined only if the theme has not defined it already, so this file never
  overrides the original code. Product forms keep their native submit (cart page / cart app).
*/
(() => {
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  /* − / + quantity buttons */
  define('quantity-input', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.querySelectorAll('button[name="plus"], button[name="minus"]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const step = Number(this.input.step) || 1;
          const min = Number(this.input.min) || 1;
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const next = (Number(this.input.value) || min) + (button.name === 'plus' ? step : -step);
          this.input.value = Math.min(max, Math.max(min, next));
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
          this.refresh();
        });
      });
      this.input.addEventListener('change', () => this.refresh());
      this.refresh();
    }
    refresh() {
      const value = Number(this.input.value);
      const min = Number(this.input.min) || 1;
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const minus = this.querySelector('button[name="minus"]');
      const plus = this.querySelector('button[name="plus"]');
      if (minus) minus.classList.toggle('disabled', value <= min);
      if (plus) plus.classList.toggle('disabled', value >= max);
    }
  });

  /* Variant pickers: find the variant, update the form, the URL and the parts of the page that change */
  class HokVariantPicker extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        if (event.target && event.target.name && event.target.name.startsWith('options[')) this.onChange();
      });
    }
    get variants() {
      if (!this._variants) {
        const json = this.querySelector('script[type="application/json"]');
        this._variants = json ? JSON.parse(json.textContent) : [];
      }
      return this._variants;
    }
    selectedOptions() {
      const values = [];
      const seen = new Set();
      this.querySelectorAll('[name^="options["]').forEach((field) => {
        if (seen.has(field.name)) return;
        seen.add(field.name);
        if (field.type === 'radio') {
          const checked = this.querySelector(`[name="${CSS.escape(field.name)}"]:checked`);
          values.push(checked ? checked.value : null);
        } else {
          values.push(field.value);
        }
      });
      return values;
    }
    async onChange() {
      const sid = this.dataset.section;
      const values = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((option, i) => option === values[i]));
      const buttons = [document.getElementById(`ProductSubmitButton-${sid}`), document.getElementById(`SectionAtcBtn-${sid}`)].filter(Boolean);
      if (!variant) {
        buttons.forEach((b) => b.setAttribute('disabled', 'disabled'));
        return;
      }
      document.querySelectorAll(`#product-form-${sid} input[name="id"], #product-form-installment-${sid} input[name="id"]`).forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      if (this.dataset.updateUrl !== 'false' && this.dataset.url) {
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
      }
      try {
        const gallery = document.getElementById(`MediaGallery-${sid}`);
        if (gallery && variant.featured_media && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(`${sid}-${variant.featured_media.id}`, true);
        }
      } catch (e) { /* gallery keeps its current image */ }
      const url = `${this.dataset.url || window.location.pathname}?variant=${variant.id}&section_id=${encodeURIComponent(this.dataset.originalSection || sid)}`;
      try {
        const html = new DOMParser().parseFromString(await (await fetch(url)).text(), 'text/html');
        [`price-${sid}`, `Inventory-${sid}`, `Sku-${sid}`, `sticky-atc-price-${sid}`].forEach((id) => {
          const fresh = html.getElementById(id);
          const current = document.getElementById(id);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
        buttons.forEach((button) => {
          const fresh = html.getElementById(button.id);
          if (!fresh) return;
          button.innerHTML = fresh.innerHTML;
          if (fresh.hasAttribute('disabled')) button.setAttribute('disabled', 'disabled');
          else button.removeAttribute('disabled');
        });
      } catch (e) {
        buttons.forEach((b) => (variant.available ? b.removeAttribute('disabled') : b.setAttribute('disabled', 'disabled')));
      }
    }
  }
  define('variant-selects', class extends HokVariantPicker {});
  define('variant-radios', class extends HokVariantPicker {});

  /* Sticky add-to-cart bar: submits the main product form, or scrolls to it; shows after scrolling */
  define('sticky-atc', class extends HTMLElement {
    connectedCallback() {
      const sid = this.dataset.section;
      const form = document.getElementById(`product-form-${sid}`);
      const atc = this.querySelector(`#SectionAtcBtn-${sid}, .main-product-atc`);
      if (atc) {
        atc.addEventListener('click', (event) => {
          event.preventDefault();
          // the theme also binds this button (inline script: adds qty 1, ignores quantity and gift options):
          // this handler runs first and is the only one, so one tap = one add
          event.stopImmediatePropagation();
          if (!form || atc.disabled) return;
          const submit = form.querySelector('[type="submit"][name="add"]');
          atc.classList.add('loading');
          setTimeout(() => atc.classList.remove('loading'), 2500);
          if (submit && !submit.disabled) submit.click();
          else if (form.requestSubmit) form.requestSubmit();
          else form.submit();
        });
      }
      const scrollButton = this.querySelector('.sticky-atc__scroll-btn');
      if (scrollButton) {
        scrollButton.addEventListener('click', (event) => {
          event.preventDefault();
          const destination = (this.dataset.scrollDestination || '').replace(/-id$/, `-${sid}`);
          const target = (destination && document.querySelector(destination)) || document.getElementById(`ProductInfo-${sid}`);
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      if (this.dataset.afterScroll === 'true' && 'IntersectionObserver' in window) {
        const watched = document.getElementById(`ProductSubmitButton-${sid}`) || form;
        if (watched) {
          new IntersectionObserver(([entry]) => {
            const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.style.transform = passed ? 'translateY(0)' : '';
            this.lift(passed);
          }).observe(watched);
        }
      }
    }
    /* while the bar is shown: floating buttons (WhatsApp app, back to top) move above it */
    lift(on) {
      const h = on ? Math.round(this.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--hok-sticky-h', `${h}px`);
      document.body.classList.toggle('hok-sticky-on', on);
      document.querySelectorAll('gty-whatsapp-chat-button').forEach((host) => {
        const box = host.shadowRoot && host.shadowRoot.querySelector('.gty-wacb-wrapper');
        if (!box) return;
        if (!box.dataset.hokBottom) box.dataset.hokBottom = getComputedStyle(box).bottom;
        box.style.transition = 'bottom .25s ease';
        box.style.bottom = on ? `calc(${box.dataset.hokBottom} + ${h}px)` : '';
      });
    }
  });

  /*
    Search suggestions while typing (theme section "predictive-search").
    - suggestions, collections and up to 8 products, from the first letter; typo-tolerant; finds references (SKU)
    - arrow keys / Enter / Escape; recent searches on focus (kept on this device only)
    - Enter or "Voir tous les résultats" submits the form to /search?q=…, which Shopify records in its search reports;
      product links keep Shopify's search tracking parameters.
  */
  const searchCss = document.createElement('style');
  searchCss.textContent = ".predictive-search:has(.hokps){max-height:min(72vh,64rem);overflow-y:auto;overscroll-behavior:contain;background-color:rgb(var(--color-background));box-shadow:0 1.2rem 3.2rem rgba(var(--color-foreground),.12)}@media screen and (max-width:749px){.hokps__side .hokps__list:first-of-type li:nth-child(n+4){display:none}}.hokps{--hokps-line:rgba(var(--color-foreground),.08);--hokps-soft:rgba(var(--color-foreground),.04);font-size:1.4rem}.hokps__grid{display:grid;grid-template-columns:minmax(0,1fr);gap:.4rem 0;padding:1.2rem 0 .4rem}.hokps__heading{margin:0;padding:.6rem 1.6rem;font-size:1.15rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:rgba(var(--color-foreground),.6)}.hokps__list,.hokps__products{list-style:none;margin:0 0 .8rem;padding:0}.hokps__term{display:flex;align-items:center;gap:1.2rem;min-height:4.4rem;padding:.6rem 1.6rem;color:rgb(var(--color-foreground));text-decoration:none;line-height:1.3}.hokps__term svg{flex:none;color:rgba(var(--color-foreground),.5)}.hokps__term img{flex:none;width:3.2rem;height:3.2rem;object-fit:cover;border-radius:.6rem}.hokps__term mark{background:none;color:inherit;font-weight:700}.hokps__term--collection span{font-weight:600}.hokps__products{display:grid;grid-template-columns:minmax(0,1fr);gap:.2rem}.hokps__product{display:grid;grid-template-columns:6.4rem minmax(0,1fr);align-items:center;gap:1.2rem;padding:.8rem 1.6rem;color:rgb(var(--color-foreground));text-decoration:none}.hokps__thumb{display:grid;place-items:center;width:6.4rem;height:6.4rem;border-radius:var(--product-card-corner-radius,.8rem);background:var(--hokps-soft);overflow:hidden}.hokps__thumb img{width:100%;height:100%;object-fit:contain}.hokps__info{display:grid;gap:.4rem;min-width:0}.hokps__title{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;font-size:1.35rem;line-height:1.35}.hokps__price b{font-weight:700}.hokps__price s{margin-left:.4rem;color:rgba(var(--color-foreground),.5);font-size:1.2rem}.hokps__soldout{font-size:1.2rem;color:rgba(var(--color-foreground),.6)}.hokps__term:hover,.hokps__product:hover,.hokps [aria-selected=\"true\"] > a,.hokps__all[aria-selected=\"true\"]{background:var(--hokps-soft)}.hokps__product:hover .hokps__title{text-decoration:underline;text-underline-offset:.3rem}.hokps__foot{border-top:.1rem solid var(--hokps-line);padding:.8rem}.hokps__all{display:flex;align-items:center;justify-content:center;gap:.8rem;width:100%;min-height:4.8rem;padding:0 1.6rem;border:0;border-radius:var(--buttons-radius,.8rem);background:rgb(var(--color-foreground));color:rgb(var(--color-background));font:inherit;font-weight:600;cursor:pointer}.hokps__all:hover,.hokps__all[aria-selected=\"true\"]{opacity:.88}.hokps__clear{display:block;margin:0 auto;min-height:4.4rem;padding:0 1.6rem;border:0;background:none;font:inherit;font-size:1.3rem;text-decoration:underline;text-underline-offset:.3rem;color:rgba(var(--color-foreground),.7);cursor:pointer}.hokps__empty{padding:2.4rem 1.6rem 1.2rem;color:rgba(var(--color-foreground),.75)}.hokps__empty p{margin:0 0 .4rem}.hokps__empty-title{font-weight:700;color:rgb(var(--color-foreground))}@media screen and (min-width:750px){.hokps__products{grid-template-columns:repeat(2,minmax(0,1fr))}.hokps__grid{grid-template-columns:minmax(18rem,26rem) minmax(0,1fr);gap:0}.hokps__grid--no-side,.hokps__grid--no-products{grid-template-columns:minmax(0,1fr)}.hokps__side{border-right:.1rem solid var(--hokps-line);padding-right:.4rem}.hokps__grid--no-products .hokps__side{border-right:0}.hokps__foot{padding:1.2rem 1.6rem}.hokps__all{width:auto;margin-left:auto;margin-right:auto}}";
  document.head.appendChild(searchCss);
  const RECENT_KEY = 'hok_recent_searches';
  const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; } };
  const saveRecent = (q) => {
    q = (q || '').trim();
    if (q.length < 2) return;
    try { localStorage.setItem(RECENT_KEY, JSON.stringify([q, ...readRecent().filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, 6))); } catch (e) { /* storage off */ }
  };
  const escapeHtml = (t) => t.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const suggestParams = '&section_id=predictive-search&resources[type]=product,collection,query&resources[limit]=8&resources[limit_scope]=each'
    + '&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.sku,vendor,tag';

  define('predictive-search', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-search]');
      this.reset = this.querySelector('button[type="reset"]');
      this.form = this.querySelector('form');
      this.status = this.querySelector('.predictive-search-status');
      if (!this.input || !this.results) return;
      this.cache = new Map();
      let timer;
      this.input.setAttribute('autocomplete', 'off');
      this.input.addEventListener('input', () => {
        const q = this.input.value.trim();
        if (this.reset) this.reset.classList.toggle('hidden', !q);
        clearTimeout(timer);
        if (!q) { this.showRecent(); return; }
        timer = setTimeout(() => this.search(q), this.cache.has(q.toLowerCase()) ? 0 : 180);
      });
      this.input.addEventListener('focus', () => {
        const q = this.input.value.trim();
        if (!q) this.showRecent();
        else if (this.results.querySelector('.hokps')) this.open();
        else this.search(q);
      });
      this.input.addEventListener('keydown', (event) => this.onKey(event));
      if (this.form) this.form.addEventListener('submit', () => saveRecent(this.input.value));
      this.results.addEventListener('click', (event) => {
        const recent = event.target.closest('[data-recent]');
        if (recent) {
          event.preventDefault();
          this.input.value = recent.dataset.recent;
          if (this.form) { saveRecent(this.input.value); this.form.submit(); }
          return;
        }
        if (event.target.closest('[data-clear-recent]')) {
          event.preventDefault();
          try { localStorage.removeItem(RECENT_KEY); } catch (e) { /* storage off */ }
          this.close();
          this.input.focus();
          return;
        }
        if (event.target.closest('a')) saveRecent(this.input.value);
      });
      if (this.reset) {
        this.reset.addEventListener('click', (event) => {
          event.preventDefault();
          this.input.value = '';
          this.reset.classList.add('hidden');
          this.showRecent();
          this.input.focus();
        });
      }
      this.addEventListener('keyup', (event) => { if (event.key === 'Escape') { this.close(); this.input.blur(); } });
      document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.close(); });
    }
    async search(q) {
      const key = q.toLowerCase();
      if (this.cache.has(key)) { this.render(this.cache.get(key), q); return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setAttribute('loading', '');
      try {
        const text = await (await fetch(`${root}search/suggest?q=${encodeURIComponent(q)}${suggestParams}`, { signal: this.controller.signal })).text();
        const markup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!markup) return;
        this.cache.set(key, markup.innerHTML);
        if (this.input.value.trim() === q) this.render(markup.innerHTML, q);
      } catch (e) {
        /* aborted or offline: Enter still submits the search form */
      } finally {
        this.removeAttribute('loading');
      }
    }
    render(html) {
      this.results.innerHTML = html;
      const count = this.results.querySelector('[data-predictive-search-live-region-count-value]');
      if (this.status && count) this.status.textContent = count.textContent.trim();
      this.open();
    }
    showRecent() {
      const recent = readRecent();
      if (!recent.length) { this.close(); return; }
      this.results.innerHTML = '<div id="predictive-search-results" class="hokps" role="listbox" aria-label="Recherches récentes"><div class="hokps__grid hokps__grid--no-products"><div class="hokps__side">'
        + '<p class="hokps__heading">Vos recherches récentes</p><ul class="hokps__list" role="group">'
        + recent.map((t) => `<li role="option" aria-selected="false"><a class="hokps__term" href="${root}search?q=${encodeURIComponent(t)}" data-recent="${escapeHtml(t)}" tabindex="-1"><svg aria-hidden="true" viewBox="0 0 20 20" width="16" height="16"><circle cx="10" cy="10" r="7" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M10 6v4l2.5 2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg><span>${escapeHtml(t)}</span></a></li>`).join('')
        + '</ul></div></div><div class="hokps__foot"><button type="button" class="hokps__clear" data-clear-recent>Effacer l’historique</button></div></div>';
      this.open();
    }
    options() { return [...this.results.querySelectorAll('[role="option"]')].filter((el) => el.offsetParent !== null); }
    onKey(event) {
      if (!this.hasAttribute('open') || !['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
      const items = this.options();
      const current = items.findIndex((el) => el.getAttribute('aria-selected') === 'true');
      if (event.key === 'Enter') {
        if (current < 0) return; // plain Enter = submit the search form
        event.preventDefault();
        const target = items[current].matches('a, button') ? items[current] : items[current].querySelector('a, button');
        if (target) target.click();
        return;
      }
      event.preventDefault();
      const next = event.key === 'ArrowDown' ? (current + 1) % items.length : (current <= 0 ? items.length - 1 : current - 1);
      items.forEach((el, i) => el.setAttribute('aria-selected', i === next ? 'true' : 'false'));
      if (items[next]) {
        items[next].scrollIntoView({ block: 'nearest' });
        this.input.setAttribute('aria-activedescendant', items[next].id || '');
      }
    }
    open() { this.setAttribute('open', ''); this.input.setAttribute('aria-expanded', 'true'); }
    close() {
      this.removeAttribute('open');
      if (this.input) { this.input.setAttribute('aria-expanded', 'false'); this.input.removeAttribute('aria-activedescendant'); }
    }
  });

  /* Touch screens: no hover zoom / image swap on product cards, so the first tap opens the product */
  const touchStyle = document.createElement('style');
  touchStyle.textContent = '@media (hover: none) { .card-wrapper:hover .media.media--hover-effect > img, .card:hover .media.media--hover-effect > img { transform: none !important; } .card-wrapper:hover .media.media--hover-effect > img:first-child:not(:only-child) { opacity: 1 !important; } .card-wrapper:hover .media.media--hover-effect > img + img { opacity: 0 !important; } }';
  document.head.appendChild(touchStyle);
  const stickyLift = document.createElement('style');
  stickyLift.textContent = 'body.hok-sticky-on .scroll-to-top-btn { transform: translateY(calc(-1 * var(--hok-sticky-h, 0px))); transition: transform .25s ease; }';
  document.head.appendChild(stickyLift);

  window.hokCore = { root };
})();
